const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"] as const;
const WEEKDAYS_LONG = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
] as const;
const MONTHS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
] as const;
const MONTHS_SHORT = [
  "jan",
  "fev",
  "mar",
  "abr",
  "mai",
  "jun",
  "jul",
  "ago",
  "set",
  "out",
  "nov",
  "dez",
] as const;

export function toDateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function parseDateKey(key: string): { year: number; month: number; day: number } {
  const [y, m, d] = key.split("-").map(Number);
  return { year: y, month: m - 1, day: d };
}

export function todayKey(): string {
  const now = new Date();
  return toDateKey(now.getFullYear(), now.getMonth(), now.getDate());
}

export function formatDateLabel(key: string): string {
  const { year, month, day } = parseDateKey(key);
  const date = new Date(year, month, day);
  const weekday = WEEKDAYS[date.getDay()];
  return `${weekday}, ${day} de ${MONTHS[month]} de ${year}`;
}

export function formatColumnDate(key: string): string {
  const { month, day } = parseDateKey(key);
  return `${day} ${MONTHS_SHORT[month]}`;
}

export function formatWeekdayLong(key: string): string {
  const { year, month, day } = parseDateKey(key);
  const date = new Date(year, month, day);
  return WEEKDAYS_LONG[date.getDay()];
}

export function formatShortDate(key: string): string {
  const { day, month } = parseDateKey(key);
  return `${String(day).padStart(2, "0")}/${String(month + 1).padStart(2, "0")}`;
}

export function formatMonthYear(year: number, month: number): string {
  return `${MONTHS[month]} ${year}`;
}

/** Segunda-feira da semana que contém a data informada. */
export function getWeekStartFromKey(key: string): string {
  const { year, month, day } = parseDateKey(key);
  const date = new Date(year, month, day);
  const weekday = date.getDay();
  const diff = weekday === 0 ? -6 : 1 - weekday;
  date.setDate(date.getDate() + diff);
  return toDateKey(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDaysToKey(key: string, days: number): string {
  const { year, month, day } = parseDateKey(key);
  const date = new Date(year, month, day);
  date.setDate(date.getDate() + days);
  return toDateKey(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Segunda a sexta da semana iniciando na segunda informada. */
export function getWorkWeekDays(weekStartKey: string): string[] {
  const keys: string[] = [];
  for (let i = 0; i < 5; i++) {
    keys.push(addDaysToKey(weekStartKey, i));
  }
  return keys;
}

/** Segunda a domingo (7 dias) da semana iniciando na segunda informada. */
export function getFullWeekDays(weekStartKey: string): string[] {
  const keys: string[] = [];
  for (let i = 0; i < 7; i++) {
    keys.push(addDaysToKey(weekStartKey, i));
  }
  return keys;
}

export function formatWeekRange(weekStartKey: string): string {
  const endKey = addDaysToKey(weekStartKey, 4);
  const start = parseDateKey(weekStartKey);
  const end = parseDateKey(endKey);
  if (start.month === end.month) {
    return `${start.day} – ${end.day} de ${MONTHS[start.month]} ${start.year}`;
  }
  return `${start.day} ${MONTHS_SHORT[start.month]} – ${end.day} ${MONTHS_SHORT[end.month]} ${end.year}`;
}

export function formatFullWeekRange(weekStartKey: string): string {
  const endKey = addDaysToKey(weekStartKey, 6);
  const start = parseDateKey(weekStartKey);
  const end = parseDateKey(endKey);
  if (start.month === end.month) {
    return `${start.day} – ${end.day} de ${MONTHS[start.month]} ${start.year}`;
  }
  return `${start.day} ${MONTHS_SHORT[start.month]} – ${end.day} ${MONTHS_SHORT[end.month]} ${end.year}`;
}

export function formatWeekdayShort(key: string): string {
  const { year, month, day } = parseDateKey(key);
  const date = new Date(year, month, day);
  return WEEKDAYS[date.getDay()].toUpperCase();
}

export function dayNumber(key: string): number {
  return parseDateKey(key).day;
}

export function horarioToMinutes(horario: string | null): number | null {
  if (!horario || !/^\d{2}:\d{2}$/.test(horario)) return null;
  const [h, m] = horario.split(":").map(Number);
  return h * 60 + m;
}

export { WEEKDAYS, MONTHS };
